import { post } from "@src/appV2/api/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import {
  useMutation,
  type UseMutationOptions,
  type UseMutationResult,
} from "@tanstack/react-query";
import axios, { type AxiosError } from "axios";
import { z } from "zod";

const presignedUploadUrlRequestSchema = z.object({
  mediaType: z.literal("VIDEO"),
  mimeType: z.string().optional(),
});

const presignedUploadUrlResponseSchema = z.object({
  data: z.object({
    id: z.string(),
    type: z.literal("presigned-upload-url"),
    attributes: z.object({
      uploadUrl: z.string(),
      fileKey: z.string(),
    }),
  }),
});

const uploadReviewMediaRequestSchema = z.object({
  workplaceId: z.string(),
  mediaType: z.literal("VIDEO"),
  mimeType: z.string().optional(),
  fileBlob: z.instanceof(Blob),
});

export type UploadReviewMediaParams = z.infer<typeof uploadReviewMediaRequestSchema>;

export interface UploadReviewMediaResponse {
  fileKey: string;
}

async function getPresignedReviewMediaUploadUrl(params: {
  workplaceId: string;
  mediaType: "VIDEO";
  mimeType?: string;
}) {
  const { workplaceId, mediaType, mimeType } = params;

  const response = await post({
    url: `${environmentConfig.REACT_APP_SHIFT_REVIEW_API_URL}/workplaces/${workplaceId}/comment-media/presigned-upload-url`,
    data: { mediaType, mimeType },
    requestSchema: presignedUploadUrlRequestSchema,
    responseSchema: presignedUploadUrlResponseSchema,
  });

  return response.data.data.attributes;
}

export function useUploadReviewMedia(
  options: UseMutationOptions<UploadReviewMediaResponse, AxiosError, UploadReviewMediaParams> = {}
): UseMutationResult<UploadReviewMediaResponse, AxiosError, UploadReviewMediaParams> {
  return useMutation<UploadReviewMediaResponse, AxiosError, UploadReviewMediaParams>({
    mutationFn: async (params) => {
      uploadReviewMediaRequestSchema.parse(params);

      const { workplaceId, mediaType, mimeType, fileBlob } = params;

      const { uploadUrl, fileKey } = await getPresignedReviewMediaUploadUrl({
        workplaceId,
        mediaType,
        mimeType,
      });

      await axios.put(uploadUrl, fileBlob, {
        headers: {
          "Content-Type": mimeType ?? fileBlob.type ?? "video/mp4",
        },
      });

      return { fileKey };
    },
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.PRESIGNED_URL_UPLOAD_FAILURE,
    },
    ...options,
  });
}
